import type { ComponentType } from 'react';
import {
  ArticleOutlined,
  DescriptionOutlined,
  FolderZipOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
  SlideshowOutlined,
  TableChartOutlined,
} from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { FileTypeIconProps } from './FileTypeIcon.props';

interface FileTypeIconMetadata {
  Icon: ComponentType<SvgIconProps>;
  getColor: (theme: Theme) => string;
}

const getFileTypeIconMetadata = (fileType?: string | null): FileTypeIconMetadata => {
  // Keep this primitive API-neutral so generated file models do not leak into shared UI.
  switch (fileType?.toLowerCase()) {
    case 'pdf':
      return {
        Icon: PictureAsPdfOutlined,
        getColor: theme => theme.palette.error.main,
      };
    case 'csv':
    case 'numbers':
    case 'xls':
    case 'xlsb':
    case 'xlsm':
    case 'xlsx':
      return {
        Icon: TableChartOutlined,
        getColor: theme => theme.palette.success.main,
      };
    case 'doc':
    case 'docm':
    case 'docx':
    case 'pages':
      return {
        Icon: DescriptionOutlined,
        getColor: theme => theme.palette.info.main,
      };
    case 'key':
    case 'ppsm':
    case 'ppt':
    case 'pptm':
    case 'pptx':
      return {
        Icon: SlideshowOutlined,
        getColor: theme => theme.palette.warning.main,
      };
    case 'gif':
    case 'jpeg':
    case 'jpg':
    case 'png':
      return {
        Icon: ImageOutlined,
        getColor: theme => theme.palette.brand[500],
      };
    case 'zip':
      return {
        Icon: FolderZipOutlined,
        getColor: theme => theme.palette.neutral[700],
      };
    case 'md':
    case 'rtf':
    case 'txt':
      return {
        Icon: ArticleOutlined,
        getColor: theme => theme.palette.neutral[600],
      };
    default:
      return {
        Icon: InsertDriveFileOutlined,
        getColor: theme => theme.palette.neutral[500],
      };
  }
};

const getSxOverrides = (sx: FileTypeIconProps['sx']) => {
  if (sx === undefined) {
    return [];
  }

  return Array.isArray(sx) ? sx : [sx];
};

const FileTypeIcon = ({ fileType, sx, ...props }: FileTypeIconProps) => {
  const theme = useTheme();
  const { Icon, getColor } = getFileTypeIconMetadata(fileType);
  const sxOverrides = getSxOverrides(sx);

  return (
    <Icon
      fontSize="inherit"
      sx={[
        { color: getColor(theme) },
        ...sxOverrides,
      ]}
      {...props}
    />
  );
};

export default FileTypeIcon;
